'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { useReadContract } from 'wagmi';

import { kycManagerAbi } from '@/abi';
import { useGlobalData } from '@/providers/GlobalDataProvider';
import { createPersonaInquiryAction } from '@/server/actions/kyc/createPersonaInquiry.action';
import { C_KYC_LIMIT, C_KYC_APPROACHING_THRESHOLD } from '@/ts/constants/C_KYC';
import { C_KYC_MANAGER_CONTRACT } from '@/ts/constants/C_KYC_MANAGER_CONTRACT';

const PERSONA_ENVIRONMENT: 'sandbox' | 'production' =
  process.env.NEXT_PUBLIC_PERSONA_ENVIRONMENT === 'production'
    ? 'production'
    : 'sandbox';

interface I_PersonaClient {
  open: () => void;
  render: () => void;
  destroy: () => void;
}

export function usePersonaKYC() {
  const user = useGlobalData('user');
  const [isKycLoading, setIsKycLoading] = useState(false);
  const [personaInquiryId, setPersonaInquiryId] = useState<string | null>(null);
  const clientRef = useRef<I_PersonaClient | null>(null);
  const closedByUserRef = useRef(false);

  const isMaxKycLevel = user?.currentKycLevel === 3;
  const kycLimit = isMaxKycLevel
    ? Infinity
    : (C_KYC_LIMIT[user?.currentKycLevel ?? 0] ?? C_KYC_LIMIT[0]);
  const totalSpent = Number(user?.totalUsdcSpent) || 0;

  const { data: isKycRestrictionsEnabled } = useReadContract({
    address: C_KYC_MANAGER_CONTRACT,
    abi: kycManagerAbi,
    functionName: 'isKycRestrictionsEnabled'
  });

  const kycEnabled = isKycRestrictionsEnabled;

  const needsKYC = kycEnabled
    ? isMaxKycLevel
      ? false
      : totalSpent >= kycLimit
    : false;

  const approachingLimit = kycEnabled
    ? isMaxKycLevel
      ? false
      : totalSpent >= kycLimit * C_KYC_APPROACHING_THRESHOLD
    : false;

  const clearPersona = useCallback(() => {
    closedByUserRef.current = true;
    try {
      clientRef.current?.destroy();
    } catch {
      // already torn down
    }
    clientRef.current = null;
    setPersonaInquiryId(null);
    setIsKycLoading(false);
  }, []);

  useEffect(() => {
    if (!personaInquiryId) return;
    if (typeof window === 'undefined') return;
    closedByUserRef.current = false;

    let isCancelled = false;
    let mountedClient: I_PersonaClient | null = null;

    const initPersona = async () => {
      const { Client } = await import('persona');

      if (isCancelled) return;

      const client = new Client({
        inquiryId: personaInquiryId,
        environment: PERSONA_ENVIRONMENT,
        onReady: () => {
          client.open();
          setIsKycLoading(false);
        },
        onComplete: () => clearPersona(),
        onCancel: () => clearPersona(),
        onError: () => clearPersona()
      });

      mountedClient = client;
      clientRef.current = client;
      client.render();
    };

    void initPersona();

    return () => {
      isCancelled = true;
      if (!closedByUserRef.current) {
        try {
          mountedClient?.destroy();
        } catch {
          // ignore
        }
      }
      clientRef.current = null;
    };
  }, [personaInquiryId, clearPersona]);

  const openPersonaModal = useCallback(async () => {
    if (isKycLoading) return;
    const level = Math.min(3, Math.max(1, (user?.currentKycLevel ?? 0) + 1)) as
      | 1
      | 2
      | 3;
    setIsKycLoading(true);
    try {
      const response = await createPersonaInquiryAction({ level });
      const payload = response?.data;
      const inquiryId =
        payload && typeof payload === 'object' && 'inquiryId' in payload
          ? (payload as { inquiryId?: string }).inquiryId
          : undefined;
      if (inquiryId) {
        setPersonaInquiryId(inquiryId);
      } else {
        setIsKycLoading(false);
      }
    } catch {
      setIsKycLoading(false);
    }
  }, [isKycLoading, user?.currentKycLevel]);

  return {
    needsKYC,
    approachingLimit,
    isKycLoading,
    openPersonaModal,
    remainingAmount: isMaxKycLevel
      ? Infinity
      : Math.max(0, kycLimit - totalSpent),
    kycLimit,
    isKycRestrictionsEnabled: kycEnabled
  };
}
